<!--
    WorkIQ "Your working patterns" (PLT-1866), restyled onto the same anatomy as the insights card
    (PLT-2189) so the two WorkIQ sections read as a pair: section header with a "Powered by Work IQ"
    chip, then an InsightCardLayout with a meta line, serif headline, prose summary, separator, and a
    footer holding a secondary affordance on the left and the CTA on the right.

    Reuse only, same rule as the insights card: InsightCardLayout, InsightCategoryLabel and
    DiveDeeperButton are imported read-only and nothing under ~vue/Labs is modified.
-->
<template>
    <section data-testid="home-mywork-progress-card">
        <div class="mt-0.5 flex items-start justify-between gap-3">
            <h3 class="scaffold-body text-ink">
                <i class="text-lg/none mr-1 bi bi-graph-up-arrow" aria-hidden="true"></i>
                Your working patterns
            </h3>
            <SourceChip>Powered by Work IQ</SourceChip>
        </div>

        <InsightCardLayout class="mt-4">
            <div class="mb-3 flex flex-wrap items-center gap-1.5">
                <InsightCategoryLabel :label="`${progress.weeks}-week trend`" :tier="WORKIQ_TIER" />
                <span class="scaffold-details-small text-ink-muted">• {{ progress.period_range_label }}</span>
            </div>

            <div class="mb-3 flex min-h-14 items-center">
                <h4 class="display-small-serif text-ink">{{ headline }}</h4>
            </div>

            <p class="body-small text-ink-muted">{{ progress.headline.summary }}</p>

            <ul class="mt-4 flex flex-col gap-2">
                <li v-for="metric in progress.metrics" :key="metric.metric" class="flex items-center justify-between gap-3">
                    <span class="body-small text-ink">{{ metric.label }}</span>
                    <span class="body-small-semibold" :class="trendClass(metric.trend)">
                        {{ formatValue(metric.recent) }}
                        <span v-if="metric.pct_change !== null">({{ formatPct(metric.pct_change) }})</span>
                    </span>
                </li>
            </ul>

            <hr class="mt-4 border-border" />

            <div class="mt-3 flex items-center justify-between gap-3">
                <BaseDialog
                    :title="`Your ${progress.weeks}-week trends`"
                    :description="`Evidence behind ${headline.toLowerCase()}`"
                    dialog-content-class="max-w-2xl p-6"
                    show-close
                    float-close>
                    <template #trigger>
                        <button
                            type="button"
                            class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-ink transition-colors hover:bg-surface-hover focus-ring"
                            data-testid="home-mywork-progress-detail-trigger">
                            <i class="bi bi-graph-up text-[11px]/none" aria-hidden="true"></i>
                            <span>See {{ progress.weeks }}-week trends</span>
                        </button>
                    </template>
                    <HomeMyWorkTrendsDetail :progress="progress" :headline="progress.headline" />
                </BaseDialog>

                <DiveDeeperButton data-testid="home-mywork-progress-start" :loading="starting" @click="startChat" />
            </div>
            <span v-if="error" role="alert" class="details mt-1 text-status-error">{{ error }}</span>
        </InsightCardLayout>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseDialog from "~vue/components/BaseDialog.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import type { InsightTier } from "~vue/Labs/insightCategory";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import type { MyWorkProgressData } from "~vue/types/chat_home";
import { openUrl } from "~vue/utils";
import { computed, ref } from "vue";

import HomeMyWorkTrendsDetail from "./HomeMyWorkTrendsDetail.vue";

const props = withDefaults(
    defineProps<{
        progress: MyWorkProgressData;
        startUrl?: string;
    }>(),
    { startUrl: "/workiq/api/progress-chat/" },
);

// Tier 4 is already "Work IQ" in the existing tier system, so both WorkIQ cards get the same dot.
const WORKIQ_TIER: InsightTier = 4;

const starting = ref(false);
const error = ref<string | null>(null);

const headline = computed(() => {
    const { label, pct_change, phrase } = props.progress.headline;
    // The server only supplies a phrase for a metric whose direction it is willing to characterize;
    // everything else falls back to a bare, verdict-free statement of the movement.
    if (phrase) {
        return phrase;
    }
    if (pct_change === null) {
        return `${label} is shifting`;
    }
    const direction = pct_change < 0 ? "down" : "up";
    return `${label} ${direction} ${Math.abs(Math.round(pct_change * 100))}%`;
});

function formatValue(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatPct(pct: number): string {
    const rounded = Math.round(pct * 100);
    return `${rounded > 0 ? "+" : ""}${rounded}%`;
}

// "changed" is a contextual metric: it moved, and we are not claiming the direction is good or bad, so
// it stays neutral rather than borrowing the green/coral verdict colours.
// Left on the legacy valence-* classes because there is no semantic token for the declining side --
// only --color-status-success exists -- and adding one means editing shared semantic.css.
function trendClass(trend: string): string {
    if (trend === "improving") {
        return "text-valence-green-800";
    }
    if (trend === "declining") {
        return "text-valence-coral-800";
    }
    return "text-ink-muted";
}

async function startChat() {
    if (starting.value) {
        return;
    }
    starting.value = true;
    error.value = null;
    try {
        const { data, response } = await useFetch(props.startUrl).post({}).json<{ coaching_session_url?: string; error?: string }>();
        if (response.value?.ok && data.value?.coaching_session_url) {
            openUrl(data.value.coaching_session_url);
        } else {
            error.value = data.value?.error || "Something went wrong. Please try again.";
        }
    } finally {
        starting.value = false;
    }
}
</script>
